// ─────────────────────────────────────────────────────────────────────────────
// PQFORCE Mobile — the TODO screens: home, list, detail, and the three sheets.
//
// ⚠ THE DETAIL SCREEN NEVER SHOWS A STATUS PICKER. It shows the transitions the
// API said this user may fire, as buttons, and says plainly when there are none.
// The lifecycle is tenant configuration, not UI: which moves exist, and who may
// make them, is a property of the tenant and the caller's relation to the item.
// Anything that reconstructs that list locally is a bug, however plausible its
// table looks — see the header of `app/mobile/types.ts`.
// ─────────────────────────────────────────────────────────────────────────────

import {
    anyGroupOpen,
    counterparty,
    namesWithSelf,
    formatActivityTime,
    groupTodos,
    initialsOf,
    isStateChange,
    commentPlaceholder,
    movesOf,
    noCommentNote,
    noTransitionNote,
    nudgeLabel,
    nudgeTargets,
    ppmContextLine,
    ppmKindLabel,
    relatedContextLine,
    relatedKindLabel,
    todoLinkLine,
    roleLabel,
    selfTransitionOf,
    sortComments,
    searchContextOf,
    searchTodos,
    openCount,
    todosForRelation,
    todosForScope,
    linkRows,
} from "home/app/mobile/todos";
import {
    type DueBucket,
    type LifecycleTransition,
    type Priority,
    type RelatedLink,
    type Todo,
    type TodoGroup,
} from "home/app/mobile/types";
import { priorityInk, priorityName, priorityOf } from "home/app/mobile/priorities";
import { dueDateText, dueFacts } from "home/app/mobile/due";
import { openDatePicker } from "home/ui/mobile/ui_mobile_datepicker";
import { currentLanguage, dateLocale, t } from "home/app/mobile/i18n";
import { type MobileState } from "home/app/mobile/store";
import { type Appearance } from "home/app/mobile/appearance";
import { type LanguageChoice } from "home/app/mobile/i18n";
import { type ListScope } from "home/app/mobile/types";
import { relationListName, relationName } from "home/app/mobile/relationNames";
import { type KnownTenant } from "home/app/mobile/tenants";

import { bind, el, fill, richTextHtml } from "home/ui/mobile/dom";
import { chevronLeft, chevronRight, icon, logo } from "home/ui/mobile/icons";
import { stateDot } from "home/ui/mobile/state";

export interface TodoActions {
    openRelation(relation: "assignee" | "owner"): void;
    /** Open the account sheet — the only route to switching installation or signing out. */
    openAccount(): void;
    /** Light, dark or the phone's own setting — applied at once, remembered on this device. */
    setAppearance(appearance: Appearance): void;
    /** German, English or the PQFORCE account's — applied at once, remembered on this device. */
    setLanguageChoice(choice: LanguageChoice): void;
    /** Switch to an installation this device is already paired with. */
    switchTenant(tenant: KnownTenant): void;
    /** Start pairing with an installation this device does not know. */
    addInstallation(): void;
    /** Sign out of the ACTIVE installation; any others stay paired. */
    signOut(): void;
    openTodos(): void;
    /** Open a TODO — from a row, a home tile or the search card, which it closes. */
    openTodo(todo: Todo): void;
    /** Open the header's search card, searching what the screen is about (`searchContextOf()`). */
    openSearch(): void;
    closeSearch(): void;
    back(): void;
    /** The list's segment: assigned to me, owned by me, or all TODOs (loaded when first chosen). */
    setRelation(relation: ListScope): void;
    toggleGrouping(): void;
    /** Fold a list group away, or open it again. Keyed by `TodoGroup.key`. */
    toggleGroup(key: string): void;
    /** Fold every one of these groups away, or open every one — the list's Collapse/Expand all. */
    setGroupsCollapsed(keys: string[], collapse: boolean): void;
    /** The New TODO sheet — with its project already set when opened from a project group. */
    openNewSheet(projectId?: string): void;
    /** The assignees sheet: add or remove one person at a time. */
    openAssigneesSheet(): void;
    /** The owner sheet: hand the TODO over to one person. */
    openOwnerSheet(): void;
    /** Ask before deleting the open TODO — offered where PQFORCE grants DELETE. */
    openDeleteSheet(): void;
    deleteTodo(): void;
    /** Pick the new owner in that sheet, without sending it. */
    pickOwner(personId: string): void;
    /** Hand the TODO over to the picked person — they replace the current owner(s). */
    handOver(): void;
    /** The owner's editor for the title and the description. */
    openDetailsSheet(): void;
    /** Rename, and rewrite the description when this owner was allowed to edit it. */
    saveDetails(title: string, description?: string): void;
    closeSheet(): void;
    /** Open the status sheet, nothing picked. */
    openStatusSheet(): void;
    /** Fire the move chosen and confirmed in the status sheet. */
    confirmTransition(transition: LifecycleTransition, label: string, note: string): void;
    /** Open the priority sheet. Owner only. */
    openPrioritySheet(): void;
    /** Pick a priority in the sheet, without sending it. */
    pickPriority(priority: Priority | ""): void;
    sendComment(text: string): void;
    nudge(): void;
    /** `""` clears the date — absence is a legitimate value, not a cancel. */
    setDueDate(iso: string): void;
    /** `""` clears the priority. Owner only; the server refuses anyone else. */
    setPriority(priority: Priority | ""): void;
    /** Add ONE assignee; the others stay. */
    assign(personId: string): void;
    /** Remove ONE assignee; the others stay. */
    unassign(personId: string): void;
    createTodo(draft: {
        title: string;
        projectId: string;
        assigneeId: string;
        /** `YYYY-MM-DD`, or `""` for no due date — a real answer, not a missing one. */
        dueISO: string;
        /** A `TODO_PRIORITIES` id, or `""` for none. */
        priority: Priority | "";
    }): void;
}

/**
 * The home header's avatar, as the way into the account sheet.
 *
 * ⚠ IT IS A BUTTON, and it is the app's only route to switching installation or signing out. It
 * was a decorative `div` — which is where the whole feature was missing from: the tenant picker
 * exists but renders on the signed-out branch, and a signed-in device cannot reach it.
 *
 * 44px, because it is now a touch target and no longer a 36px ornament; the avatar keeps its own
 * size inside it, so the header does not change shape.
 */
function accountButton(name: string, actions: TodoActions): HTMLElement {
    const button = el(
        "button",
        {
            class: "pqm-account-button",
            type: "button",
            "aria-label": t("mobileViewAccountButton", "Account and tenants"),
        },
        [avatar(name || "?")],
    );
    bind(button, "click", actions.openAccount);
    return button;
}

function avatar(name: string, size: "sm" | "md" | "lg" = "lg"): HTMLElement {
    const cls = size === "sm" ? "pqm-avatar pqm-avatar-sm" : size === "md" ? "pqm-avatar pqm-avatar-md" : "pqm-avatar";
    return el("div", { class: cls, text: initialsOf(name), "aria-hidden": "true" });
}

/**
 * The priority as a small chip FILLED with the PQFORCE client's colour for it, or null when the TODO
 * has none — then nothing is drawn, never a neutral chip: most TODOs carry no priority, and a chip on
 * every row would read as a deliberate one.
 *
 * The fill and the ink are both constants out of `TODO_PRIORITIES`, never tenant data, so writing them
 * into `style` is safe. The chip carries the word as well as the colour: colour alone says nothing to
 * a reader who cannot tell the three apart.
 */
export function priorityChip(value: Priority | null | undefined): HTMLElement | null {
    const priority = priorityOf(value);
    if (!priority) return null;

    const name = priorityName(priority.id);
    return el("span", {
        class: "pqm-prio-chip",
        style: `background:${priority.color};color:${priorityInk(priority.color)}`,
        title: t("mobilePriorityChipLabel", "{priority} priority", { priority: name }),
        text: name,
    });
}

/**
 * The tone of a due group's heading chip — a CSS class suffix, never a colour: the chips are drawn
 * from the theme's own tokens in mobile.scss, so they follow dark mode like everything else.
 */
function dueChipTone(bucket: DueBucket): string {
    switch (bucket) {
        case "Overdue":
            return "overdue";
        case "Today":
            return "today";
        case "This week":
            return "week";
        case "None":
            return "none";
        default:
            return "later";
    }
}

/** One row in the grouped list. `compact` is the home screen's shorter variant. */
function todoRow(todo: Todo, actions: TodoActions, now: number, compact = false): HTMLElement {
    const due = dueFacts(todo.dueISO, now);
    const meta: HTMLElement[] = [
        stateDot(todo),
        el("div", {
            class: `pqm-due ${due.overdue ? "pqm-due-overdue" : "pqm-due-normal"}`,
            text: due.label,
        }),
    ];
    const chip = priorityChip(todo.priority);
    if (chip) meta.push(chip);

    if (compact) {
        meta.push(el("div", { class: "pqm-row-who", style: "margin-left:auto", text: roleLabel(todo) }));
    } else {
        if (todo.comments.length > 0) {
            meta.push(
                el("span", { class: "pqm-comments" }, [
                    icon("chat", 12, "var(--color-ink-6)"),
                    document.createTextNode(String(todo.comments.length)),
                ]),
            );
        }
        // ⚠ NO NAME, NO BLOCK. A todo nobody is assigned to put the word "Assignee" beside an empty
        // avatar — a label pointing at nothing, which reads as a row that failed to load rather than
        // as a todo waiting for somebody. The detail screen is where the absence is stated in words;
        // a list row has too little space to say it. Covers the ownerless case the same way.
        const other = counterparty(todo);
        if (other.name) {
            meta.push(
                el("span", { class: "pqm-row-right" }, [
                    el("span", { class: "pqm-row-who", text: other.label }),
                    avatar(other.name, "sm"),
                ]),
            );
        }
    }

    // The code first, as PQFORCE's own lists show it: `T-12` is what people quote to each other.
    const body: HTMLElement[] = [
        el("div", { class: "pqm-row-title" }, [
            ...(todo.code ? [el("span", { class: "pqm-code pqm-code-sm", text: todo.code })] : []),
            document.createTextNode(todo.title),
        ]),
    ];
    if (!compact) {
        // ⚠ ONE LINK OR NONE, and absence is stated. This line used to read `project · task` and
        // printed " · —" on every todo the server had failed to resolve — which was all of them.
        const linked = todo.ppm || (todo.related ?? []).length > 0;
        body.push(
            el("div", {
                class: `pqm-row-sub${linked ? "" : " pqm-row-sub-none"}`,
                text: todoLinkLine(todo),
            }),
        );
    }
    body.push(el("div", { class: "pqm-row-meta" }, meta));

    // No priority bar on the left any more (removed 2026-09-28): the chip in the meta line says it,
    // in words as well as colour, and a bar only on SOME rows put their text out of line with the rest.
    const row = el("button", { class: "pqm-row", type: "button" }, [el("div", { class: "pqm-row-body" }, body)]);
    bind(row, "click", () => actions.openTodo(todo));
    return row;
}

// ── home ─────────────────────────────────────────────────────────────────────

/**
 * The ONE header every app screen wears, sticky at the top of the page.
 *
 * ⚠ SLIM ON PURPOSE. It used to be a brand row (logo + tenant) PLUS a screen head under it on every
 * screen, which on a phone was a fifth of the height before the first row of content. Now:
 *
 * - **Home** carries the brand: the logo, which installation this is, and the account button.
 * - **Every other screen** spends the row on getting back and on its own controls, with the
 *   installation as a small line under the title.
 *
 * ⚠ WHICH INSTALLATION THIS IS stays on every screen. One device can be paired with several and
 * switch between them without re-approving, and a TODO list looks much the same on any of them.
 * Host plus alias, because a tenant is both.
 */
export interface ScreenHeaderOptions {
    /** Home: the brand row. Otherwise the title row. */
    home?: { search: HTMLElement; account: HTMLElement };
    title?: string;
    /** A code shown before the title (a TODO's `T-12`). */
    code?: string;
    /** Shows a back chevron that calls this. */
    onBack?: () => void;
    /** The screen's own controls, on the right. */
    actions?: HTMLElement[];
}

export function screenHeader(state: MobileState, options: ScreenHeaderOptions): HTMLElement {
    const address = state.profile?.tenantUrl || state.profile?.tenantHost || "";

    if (options.home) {
        const children: HTMLElement[] = [logo(24)];
        if (address) children.push(el("div", { class: "pqm-appbar-tenant", title: address, text: address }));
        children.push(options.home.search, options.home.account);
        return el("header", { class: "pqm-appbar pqm-appbar-home" }, children);
    }

    const children: HTMLElement[] = [];
    if (options.onBack) {
        const back = el(
            "button",
            { class: "pqm-appbar-back", type: "button", "aria-label": t("mobileViewBack", "Back") },
            [chevronLeft()],
        );
        bind(back, "click", options.onBack);
        children.push(back);
    }

    // One line: the code (never cut), then the title with an ellipsis.
    const titleRow = options.code
        ? el("div", { class: "pqm-appbar-title-row" }, [
              el("span", { class: "pqm-code", text: options.code }),
              el("div", { class: "pqm-h pqm-appbar-title", text: options.title ?? "" }),
          ])
        : el("div", { class: "pqm-h pqm-appbar-title", text: options.title ?? "" });
    const text = el("div", { class: "pqm-appbar-text" }, [titleRow]);
    if (address) text.appendChild(el("div", { class: "pqm-appbar-sub", title: address, text: address }));
    children.push(text);

    if (options.actions?.length) children.push(el("div", { class: "pqm-appbar-actions" }, options.actions));

    return el("header", { class: "pqm-appbar" }, children);
}

/**
 * One MODULE's block on the home screen: its name, then one row per list it offers, each with its
 * count and leading to that list. TODOs are the first module; projects and tasks will add blocks of
 * the same shape, which is why this is a block and not two tiles.
 */
function moduleBlock(
    title: string,
    glyph: "checkSquare",
    rows: Array<{ label: string; count: string; onOpen: () => void }>,
): HTMLElement {
    return el("section", { class: "pqm-home-block", "aria-label": title }, [
        el("div", { class: "pqm-home-block-head" }, [
            el("span", { class: "pqm-home-block-icon", "aria-hidden": "true" }, [icon(glyph, 18)]),
            el("div", { class: "pqm-h pqm-home-block-title", text: title }),
        ]),
        ...rows.map((row) => {
            const button = el("button", { class: "pqm-home-row", type: "button" }, [
                el("span", { class: "pqm-home-row-label", text: row.label }),
                el("span", { class: "pqm-home-row-count pqm-tabular", text: row.count }),
                el("span", { class: "pqm-home-row-chevron", "aria-hidden": "true" }, [chevronRight()]),
            ]);
            bind(button, "click", row.onOpen);
            return button;
        }),
    ]);
}

/**
 * The TODOs block: how many OPEN TODOs are on this person's two lists (a state of any category but
 * `CLOSED`, `isOpenTodo()`), each named as the TENANT names it
 * (`relationListName()` — `To dos` / `Eigene Todos` on the measured tenant). Counts only, no
 * break-down per state: the list is one tap away. "…" while the first read is still on its way, so a
 * zero never stands in for "not known yet".
 */
function todosBlock(state: MobileState, actions: TodoActions): HTMLElement {
    const count = (role: "assignee" | "owner") =>
        state.loading && state.todos.length === 0 ? "…" : String(openCount(todosForRelation(state.todos, role)));

    return moduleBlock(t("mobileViewTodosTitle", "TODOs"), "checkSquare", [
        { label: scopeLabel("assignee"), count: count("assignee"), onOpen: () => actions.openRelation("assignee") },
        { label: scopeLabel("owner"), count: count("owner"), onOpen: () => actions.openRelation("owner") },
    ]);
}

export function renderHome(state: MobileState, actions: TodoActions, now: number = Date.now()): HTMLElement[] {
    // `now` is kept in the signature for the callers and tests that pass it; the home screen has no
    // dates of its own any more.
    void now;
    const body: HTMLElement[] = [todosBlock(state, actions)];

    if (state.loadError) {
        body.push(
            el("div", { class: "pqm-note" }, [
                icon("warningCircle", 15, "var(--color-ink-5)"),
                el("div", { class: "pqm-note-text", text: state.loadError }),
            ]),
        );
    }

    // ⚠ NO "See all TODOs" BUTTON. The tab bar under it reaches the list from every screen, so the
    // button was a second door to the room next door; searching is on the LIST, where the rows are.
    const profileName = state.profile?.name ?? "";

    // The greeting scrolls with the page: it is a welcome, not a control, and the header it used
    // to share a row with is now as slim as the other screens'.
    body.unshift(
        el("div", { class: "pqm-greeting" }, [
            el("div", { class: "pqm-greeting-kicker", text: greeting() }),
            el("div", {
                class: "pqm-h pqm-greeting-title",
                text: profileName
                    ? t("mobileViewHelloName", "Hello, {name}", { name: profileName.split(/\s+/)[0]! })
                    : t("mobileViewHello", "Hello"),
            }),
        ]),
    );

    return [
        screenHeader(state, {
            home: { search: searchButton(state, actions), account: accountButton(profileName, actions) },
        }),
        el("div", { class: "pqm-scroll pqm-home-body" }, body),
    ];
}

/** The kicker over the greeting — today's date, spelled the way the design shows it. */
function greeting(): string {
    const now = new Date();
    // German reads "Dienstag, 22. September" from the platform; the English keeps the design's own
    // spelling, which no locale produces exactly.
    if (currentLanguage() === "de") {
        return new Intl.DateTimeFormat(dateLocale(), { weekday: "long", day: "numeric", month: "long" }).format(now);
    }
    const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    const months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
    ];
    return `${days[now.getDay()]} ${now.getDate()} ${months[now.getMonth()]}`;
}

// ── search ───────────────────────────────────────────────────────────────────

/**
 * What is typed into the search card, across repaints.
 *
 * ⚠ NOT IN THE STORE, for the reason `assigneeQuery` is not: every store update repaints the whole
 * app, so a keystroke written there would rebuild the field under the caret. Only the card's RESULTS
 * are repainted as you type; this is read back when something else repaints the app (the TODOs
 * re-read, a toast), which would otherwise clear a search somebody is halfway through. Kept when
 * the card closes, so opening it again picks up where it was; cleared with `clearTodoSearch()` when
 * the installation changes.
 */
let searchQuery = "";

/** The header's search button, on every app screen. Its label names what it searches. */
function searchButton(state: MobileState, actions: TodoActions): HTMLElement {
    const button = el(
        "button",
        {
            class: "pqm-appbar-icon",
            type: "button",
            "aria-label": searchLabels(searchContextOf(state)).button,
            "aria-haspopup": "dialog",
        },
        [icon("magnifier", 20)],
    );
    bind(button, "click", actions.openSearch);
    return button;
}

/** The words of one search context — one today; a second module adds its own here. */
function searchLabels(_context: ReturnType<typeof searchContextOf>): {
    button: string;
    placeholder: string;
} {
    return {
        button: t("mobileViewSearchTodosButton", "Search TODOs"),
        placeholder: t("mobileViewSearchTodos", "Search code, title and description"),
    };
}

/**
 * The search card: a field and its results, dropped down over the screen from the header.
 *
 * It searches what the screen it was opened on is about (`searchContextOf()`) — TODOs, from Home,
 * the list or a TODO — and across BOTH relations: the card is for finding one, and which segment it
 * sits under is the list's business. Tapping a result opens it and closes the card; the scrim, the
 * close button, Escape and the phone's back gesture close it too.
 *
 * An overlay like a sheet (`data-overlay="search"`), so it is kept still across repaints, holds the
 * page underneath still and brings the module menu back with it.
 */
export function renderSearchCard(state: MobileState, actions: TodoActions, now: number = Date.now()): HTMLElement {
    const labels = searchLabels(searchContextOf(state));
    // ⚠ Whether the field HAD focus is read before this render replaces it.
    const hadFocus = document.activeElement?.classList.contains("pqm-search-input") ?? false;

    const input = el("input", {
        class: "input pqm-search-input",
        type: "search",
        // No placeholder: the hint under the field already says what is searched. The name stays
        // for a screen reader.
        "aria-label": labels.placeholder,
        autocomplete: "off",
        enterkeyhint: "search",
    });
    input.value = searchQuery;

    /**
     * Empties the field and keeps the card open — a filled circle inside the field, the shape phones
     * use for exactly this. Shown only while there is something to clear. The browsers' own clear
     * button on `type=search` is hidden in the stylesheet: Safari on iOS draws none, so it could not
     * be relied on, and two crosses side by side would be one too many.
     */
    const clear = el(
        "button",
        { class: "pqm-search-clear", type: "button", "aria-label": t("mobileViewClearSearch", "Clear search") },
        [icon("xCircle", 18)],
    );
    clear.hidden = searchQuery === "";
    bind(clear, "click", () => {
        searchQuery = "";
        input.value = "";
        clear.hidden = true;
        paint();
        input.focus();
    });

    // A plain CROSS, like every card's close (2026-10-18). The clear button inside the field is a
    // FILLED circle with a cross, so the two shapes stay apart.
    const close = el(
        "button",
        { class: "pqm-appbar-icon", type: "button", "aria-label": t("mobileViewCloseSearch", "Close search") },
        [icon("x", 18)],
    );
    bind(close, "click", actions.closeSearch);

    const results = el("div", { class: "pqm-search-results", role: "list" });

    /** The results for whatever is typed — rebuilt by the field itself, never through the store. */
    const paint = (): void => {
        const query = searchQuery.trim();
        if (!query) {
            fill(results, [
                el("div", {
                    class: "pqm-search-hint",
                    text: t(
                        "mobileViewSearchHint",
                        "Type to search the code, title and description of every TODO you hold.",
                    ),
                }),
            ]);
            return;
        }

        const found = searchTodos(state.todos, query);
        if (found.length === 0) {
            fill(results, [
                el("div", {
                    class: "pqm-search-hint",
                    // ⚠ A search that found nothing must say SO, naming what was searched for.
                    text: t("mobileViewNoTodoMatches", "No TODO matches “{query}”.", { query }),
                }),
            ]);
            return;
        }

        fill(results, [
            el("div", {
                class: "pqm-kicker pqm-search-count",
                text: t("mobileViewMatchCount", "{count} found", { count: found.length }),
            }),
            ...found.map((todo) => todoRow(todo, actions, now, true)),
        ]);
    };

    bind(input, "input", () => {
        searchQuery = input.value;
        clear.hidden = searchQuery === "";
        paint();
    });
    paint();

    const card = el("div", { class: "pqm-search-card", role: "dialog", "aria-label": labels.button }, [
        el("div", { class: "pqm-search-field" }, [icon("magnifier", 16, "var(--color-ink-5)"), input, clear, close]),
        results,
    ]);

    const scrim = el("div", { class: "pqm-scrim pqm-search-scrim", "data-overlay": "search", tabindex: "-1" }, [card]);
    // Only a tap on the scrim itself closes — not one inside the card that bubbles up to it.
    bind(scrim, "click", (event: Event) => {
        if (event.target === scrim) actions.closeSearch();
    });
    bind(scrim, "keydown", (event: Event) => {
        if ((event as KeyboardEvent).key === "Escape") actions.closeSearch();
    });

    window.setTimeout(() => {
        // Opening the card puts the caret in the field; a repaint gives it back only if it had it.
        if (!scrim.classList.contains("pqm-steady") || hadFocus) {
            input.focus();
            input.setSelectionRange(input.value.length, input.value.length);
        }
    }, 0);

    return scrim;
}

// ── list ─────────────────────────────────────────────────────────────────────

/** Forget the search card's query — the TODOs it found belong to an installation being left. */
export function clearTodoSearch(): void {
    searchQuery = "";
}

/** A list segment's name — the header's title, and each segment button's accessible name. */
function scopeLabel(scope: ListScope): string {
    // The tenant's own names for a person's two lists win (relationNames.ts — `To dos`,
    // `Eigene Todos` on the measured tenant); the app's words where the tenant has none.
    if (scope === "owner") return relationListName("owner", t("mobileViewIOwn", "I own"));
    if (scope === "all") return t("mobileViewAllTodos", "All TODOs");
    return relationListName("assignee", t("mobileViewAssignedToMe", "Assigned to me"));
}

export function renderList(state: MobileState, actions: TodoActions, now: number = Date.now()): HTMLElement[] {
    const add = el(
        "button",
        {
            class: "btn btn-primary btn-icon",
            style: "width:34px;height:34px",
            type: "button",
            "aria-label": t("mobileViewNewTodo", "New TODO"),
        },
        [icon("plus", 17)],
    );
    bind(add, "click", () => actions.openNewSheet());

    // ⚠ ICONS, NOT WORDS (2026-10-18), so the segments share ONE line with the count and the tools
    // instead of taking a line of their own. Each option keeps its name — as the radio's label for a
    // screen reader and as a tooltip — and the CHOSEN one is named in the header's title.
    const segment = (label: string, relation: ListScope, glyph: "person" | "crown" | "listAll") => {
        const input = el("input", {
            type: "radio",
            name: "pqm-relation",
            checked: state.relation === relation,
            "aria-label": label,
        });
        const option = el("label", { class: "seg-opt pqm-seg-icon", title: label }, [input, icon(glyph, 18)]);
        bind(input, "change", () => actions.setRelation(relation));
        return option;
    };

    // Steps through the groupings in turn (nextGrouping()). It SHOWS only the grouping's name, to fit
    // the shared line; its accessible name says the whole sentence.
    const [groupName, groupSentence] =
        state.grouping === "status"
            ? [t("mobileViewGroupingStatus", "Status"), t("mobileViewGroupedByStatus", "Grouped by Status")]
            : state.grouping === "priority"
              ? [t("mobileViewGroupingPriority", "Priority"), t("mobileViewGroupedByPriority", "Grouped by Priority")]
              : state.grouping === "project"
                ? [t("mobileViewGroupingProject", "Project"), t("mobileViewGroupedByProject", "Grouped by Project")]
                : [t("mobileViewGroupingDue", "Due date"), t("mobileViewGroupedByDue", "Grouped by Due date")];
    const group = el("button", {
        class: "btn btn-ghost pqm-group-toggle",
        type: "button",
        text: groupName,
        "aria-label": groupSentence,
        title: groupSentence,
    });
    bind(group, "click", actions.toggleGrouping);

    const count = el("div", { class: "pqm-list-count" });
    /**
     * Collapse or expand every group ON SCREEN at once. Hidden while there is no group to act on.
     */
    const foldAll = el("button", { class: "pqm-appbar-icon pqm-fold-all", type: "button" });
    let shownKeys: string[] = [];
    bind(foldAll, "click", () => {
        actions.setGroupsCollapsed(shownKeys, anyGroupOpen(state.collapsedGroups, shownKeys));
    });
    const body = el("div", { class: "pqm-list-groups" });

    const groupBlock = (g: TodoGroup): HTMLElement => {
        // ⚠ The heading is a BUTTON, not a clickable div: it has to be reachable by keyboard and
        // announce its state, and `aria-expanded` is what a screen reader reads that from. The
        // count stays visible when folded — it is the one thing a folded group still has to say.
        const collapsed = state.collapsedGroups.includes(g.key);
        const head = el(
            "button",
            {
                class: `pqm-group-head${collapsed ? " pqm-group-collapsed" : ""}`,
                type: "button",
                "aria-expanded": collapsed ? "false" : "true",
            },
            [
                el("span", { class: "pqm-group-chevron" }, [chevronRight()]),
                // A status group's state circle — the same one its rows draw. Decorative: the state's
                // name is printed right beside it, and a screen reader should hear it once.
                ...(g.state ? [stateDot(g.state, "sm", true)] : []),
                // A PRIORITY group is headed by the chip its rows carry, a DUE group by a chip coloured
                // for how pressing it is — each says the group's name, so it replaces the label
                // rather than repeating it beside it.
                priorityChip(g.priority) ??
                    (g.due
                        ? el("span", { class: `pqm-due-chip pqm-due-chip-${dueChipTone(g.due)}`, text: g.label })
                        : el("span", { class: "pqm-kicker", text: g.label })),
                el("span", { class: "pqm-group-count", text: String(g.count) }),
            ],
        );
        bind(head, "click", () => actions.toggleGroup(g.key));

        // A project group gets a + after its name: a new TODO in THIS project. Beside the heading,
        // never inside it — the heading is itself a button (fold), and buttons do not nest.
        let headRow: HTMLElement = head;
        if (g.projectId) {
            const projectId = g.projectId;
            const addHere = el(
                "button",
                {
                    class: "pqm-group-add",
                    type: "button",
                    "aria-label": t("mobileViewNewTodoInProject", "New TODO in {project}", { project: g.label }),
                },
                [icon("plus", 16)],
            );
            bind(addHere, "click", () => actions.openNewSheet(projectId));
            headRow = el("div", { class: "pqm-group-headrow" }, [head, addHere]);
        }

        return el("div", { class: "pqm-group" }, [
            headRow,
            ...(collapsed ? [] : g.items.map((todo) => todoRow(todo, actions, now))),
        ]);
    };

    /** The groups, the count and the empty line. */
    const paint = (): void => {
        const list = todosForScope(state.todos, state.allTodos, state.relation);
        const groups = groupTodos(list, state.grouping, now);

        shownKeys = groups.map((g) => g.key);
        foldAll.hidden = shownKeys.length === 0;
        // An icon, named by what it will do — collapse while any group is open, expand once none is.
        const collapse = anyGroupOpen(state.collapsedGroups, shownKeys);
        const foldLabel = collapse
            ? t("mobileViewCollapseAll", "Collapse all")
            : t("mobileViewExpandAll", "Expand all");
        foldAll.setAttribute("aria-label", foldLabel);
        foldAll.title = foldLabel;
        foldAll.replaceChildren(icon(collapse ? "collapseAll" : "expandAll", 18));

        // OPEN only (`isOpenTodo()`): the rows still include the closed ones, but they are not open.
        count.textContent = t("mobileViewOpenCount", "{count} open", { count: openCount(list) });

        const blocks = groups.map(groupBlock);
        if (groups.length === 0) {
            blocks.push(
                el("div", {
                    class: "pqm-empty",
                    text:
                        state.relation === "all" && state.allError
                            ? state.allError
                            : state.loading || (state.relation === "all" && (state.allLoading || !state.allTodos))
                              ? t("mobileViewLoading", "Loading…")
                              : state.relation === "all"
                                ? t("mobileViewNothingVisible", "There are no TODOs you can see.")
                                : state.relation === "owner"
                                  ? t(
                                        "mobileViewNothingOwned",
                                        "Nothing in “{list}” yet. TODOs you create appear here.",
                                        { list: scopeLabel("owner") },
                                    )
                                  : t("mobileViewNothingAssigned", "Nothing in “{list}” right now.", {
                                        list: scopeLabel("assignee"),
                                    }),
                }),
            );
        }
        fill(body, blocks);
    };

    paint();

    // ⚠ The segments and the tools scroll WITH the list rather than sitting under the header: they
    // are set once and then out of the way, and the rows are what the screen is for. Searching is
    // the header's (searchButton()), as on every screen.
    return [
        screenHeader(state, {
            // The CHOSEN segment, named — the segments themselves are icons now.
            title: scopeLabel(state.relation),
            actions: [searchButton(state, actions), add],
        }),
        el("div", { class: "pqm-scroll pqm-list-body" }, [
            el("div", { class: "pqm-list-tools" }, [
                el(
                    "div",
                    {
                        class: "seg pqm-seg-icons",
                        role: "radiogroup",
                        "aria-label": t("mobileViewTodosTitle", "TODOs"),
                    },
                    [
                        segment(scopeLabel("assignee"), "assignee", "person"),
                        segment(scopeLabel("owner"), "owner", "crown"),
                        // Every TODO this person may see, held or not — read when first chosen.
                        segment(scopeLabel("all"), "all", "listAll"),
                    ],
                ),
                count,
                el("div", { class: "pqm-list-tool-buttons" }, [foldAll, group]),
            ]),
            body,
        ]),
    ];
}

// ── detail ───────────────────────────────────────────────────────────────────

/**
 * The facts: what the TODO is linked to, and who it is for and by — one block per link slot
 * (`linkRows()`), each shaped like the description's: a heading, the controls at its end, the
 * values underneath (2026-10-18; they were rows of a zebra table).
 *
 * Each block is three things, and they are kept apart on purpose:
 * - the LABEL, as the block's heading;
 * - the VALUES, as their own element — where a later module hooks "open this project / task" in;
 * - at the heading's end, a separate CHANGE control (a pencil), only where the slot is editable and
 *   PQFORCE lets this person link (see linkRows()).
 *
 * So "open the linked object" and "change the link" never compete for one tap — which matters the
 * day a projects module makes the PPM link both openable and changeable.
 */
function factsTable(todo: Todo, state: MobileState, actions: TodoActions): HTMLElement {
    const self = state.profile?.resourceId ?? "";

    const change = (label: string, onClick: () => void): HTMLElement => {
        const button = el(
            "button",
            {
                class: "pqm-fact-action",
                type: "button",
                "aria-label": t("mobileViewChangeLink", "Change {label}", { label }),
            },
            [icon("pencil", 16)],
        );
        bind(button, "click", onClick);
        return button;
    };

    const row = (label: string, value: HTMLElement, tail: HTMLElement[]) => factBlock(label, value, tail);

    const rows = linkRows(todo).map(({ slot, editable }) => {
        switch (slot) {
            case "ppm":
                return ppmFact(todo);
            case "related":
                // ⚠ A SECOND ROW, never merged into the first. A TODO may belong to a task AND to a
                // risk — the PPM link answers "where does this sit", these "what is it about".
                return row(
                    t("mobileViewRelatedTo", "Related to"),
                    el("div", { class: "pqm-fact-val" }, (todo.related ?? []).map(relatedEntry)),
                    [],
                );
            case "assignee": {
                // The tenant's own name for each role wins (relationNames.ts); the app's word otherwise.
                const label = relationName(
                    "assignee",
                    todo.assignees.length > 1
                        ? t("mobileViewAssignees", "Assignees")
                        : t("mobileViewAssignee", "Assignee"),
                );
                const tail: HTMLElement[] = [];
                // Reminding the assignees: offered only with somebody to remind (every assignee but
                // you), and only to somebody allowed to look after the TODO — the server refuses
                // the rest, and a button that can only be refused is worse than none.
                const targets = nudgeTargets(todo, self);
                if (todo.permissions?.update === true && targets.length) {
                    const bell = el(
                        "button",
                        { class: "pqm-fact-action", type: "button", "aria-label": nudgeLabel(targets) },
                        [icon("bell", 16)],
                    );
                    bind(bell, "click", actions.nudge);
                    tail.push(bell);
                }
                if (editable) tail.push(change(label, actions.openAssigneesSheet));
                return row(
                    label,
                    el("div", { class: "pqm-fact-val", text: namesWithSelf(todo.assignees, self) || "—" }),
                    tail,
                );
            }
            case "owner": {
                const label = relationName(
                    "owner",
                    todo.owners.length > 1 ? t("mobileViewOwners", "Owners") : t("mobileViewOwner", "Owner"),
                );
                // ⚠ The reader reads as "You" in BOTH people rows — see namesWithSelf().
                return row(
                    label,
                    el("div", { class: "pqm-fact-val", text: namesWithSelf(todo.owners, self) || "—" }),
                    editable ? [change(label, actions.openOwnerSheet)] : [],
                );
            }
        }
    });

    return el("div", { class: "pqm-facts" }, rows);
}

/** One risk or report: the same chip-and-context shape as the PPM link, so they read alike. */
function relatedEntry(link: RelatedLink): HTMLElement {
    const children: HTMLElement[] = [
        el("div", { class: "pqm-ppm-head" }, [
            el("span", { class: "pqm-ppm-chip", text: relatedKindLabel(link.kind) }),
            el("span", { class: "pqm-ppm-name", text: link.name }),
        ]),
    ];

    const context = relatedContextLine(link);
    if (context) children.push(el("div", { class: "pqm-ppm-context", text: context }));

    return el("div", { class: "pqm-related-entry" }, children);
}

/**
 * The one "Linked to" row: a neutral kind chip, the name, and the levels above it underneath.
 *
 * ⚠ NOT two rows. A TODO has one link into PPM, and a Project row beside a Task row invites the
 * reading that it has both — which is what the previous pair did, with an em dash in whichever of
 * them did not apply.
 *
 * ⚠ Nothing linked is a SENTENCE, not an empty value. "—" in a fact row reads as a value that
 * failed to load; standing on its own is a real and ordinary state.
 */
/** One fact as a block: its heading with the controls at the end, then the values. */
function factBlock(label: string, value: HTMLElement, tail: HTMLElement[] = []): HTMLElement {
    return el("div", { class: "pqm-section pqm-fact" }, [
        el("div", { class: "pqm-section-head" }, [
            el("div", { class: "pqm-kicker pqm-fact-key", text: label }),
            ...(tail.length ? [el("div", { class: "pqm-fact-actions" }, tail)] : []),
        ]),
        value,
    ]);
}

function ppmFact(todo: Todo): HTMLElement {
    const ppm = todo.ppm ?? null;
    const label = t("mobileViewLinkedTo", "Linked to");

    if (!ppm) {
        return factBlock(
            label,
            el("div", {
                class: "pqm-fact-val pqm-ppm-none",
                text: t("mobileViewNothingLinked", "Nothing — this TODO stands on its own"),
            }),
        );
    }

    const head = el("div", { class: "pqm-ppm-head" }, [
        el("span", { class: "pqm-ppm-chip", text: ppmKindLabel(ppm.kind) }),
        // A link nothing could be named for still says it is a link — the chip carries that — so
        // the name may legitimately be empty here.
        el("span", { class: "pqm-ppm-name", text: ppm.name }),
    ]);

    const value: HTMLElement[] = [head];
    const context = ppmContextLine(ppm);
    if (context) value.push(el("div", { class: "pqm-ppm-context", text: context }));

    return factBlock(label, el("div", { class: "pqm-fact-val pqm-ppm" }, value));
}

function activitySection(todo: Todo, state: MobileState, actions: TodoActions): HTMLElement {
    // ⚠ THE RIGHT TO COMMENT IS THE RIGHT TO FIRE THE SELF-TRANSITION. The API returns only
    // transitions this user may fire, so its absence is a permission fact — the log is read-only
    // and says who is holding the item, rather than offering a field whose write the server would
    // refuse with a 422 about the installation's configuration.
    const selfMove = selfTransitionOf(todo);

    if (!selfMove) {
        return el("div", { class: "pqm-section" }, [
            el("div", { class: "pqm-kicker", text: t("mobileViewActivity", "Activity") }),
            el("div", { class: "pqm-note" }, [
                icon("warningCircle", 15, "var(--color-ink-5)"),
                el("div", { class: "pqm-note-text", text: noCommentNote(todo) }),
            ]),
            ...activityLog(todo),
        ]);
    }

    const placeholder = commentPlaceholder(selfMove);
    const input = el("input", {
        class: "input",
        style: "min-height:44px",
        type: "text",
        placeholder,
        "aria-label": placeholder,
    });
    const send = el("button", {
        class: "btn btn-primary",
        style: "min-height:44px",
        type: "button",
        text: t("mobileViewSend", "Send"),
        disabled: true,
    });

    // Enable/disable in place. Writing the draft into the store on every keystroke
    // would re-render this screen and take the caret with it.
    const sync = () => {
        send.disabled = state.commentBusy || input.value.trim().length === 0;
    };
    bind(input, "input", sync);
    sync();

    const submit = () => {
        const text = input.value.trim();
        if (!text) return;
        input.value = "";
        sync();
        actions.sendComment(text);
    };
    bind(send, "click", submit);
    bind(input, "keydown", (event) => {
        if ((event as KeyboardEvent).key === "Enter") submit();
    });

    const children: HTMLElement[] = [
        el("div", { class: "pqm-kicker", text: t("mobileViewActivity", "Activity") }),
        el("div", { class: "pqm-comment-form" }, [input, send]),
    ];

    children.push(...activityLog(todo));

    return el("div", { class: "pqm-activity" }, children);
}

/**
 * The log itself — shared by both branches of activitySection.
 *
 * Extracted when the comment field became conditional: a read-only log is still a log, and the
 * entries must render identically whether or not this user may add to them. Returns an array so a
 * log with no entries contributes nothing rather than an empty box.
 */
function activityLog(todo: Todo): HTMLElement[] {
    const comments = sortComments(todo.comments);
    if (comments.length === 0) return [];

    return [
        el(
            "div",
            { class: "pqm-comments-list" },
            comments.map((comment) => {
                // One line: what the entry did, who did it, when. A state change shows the state it
                // MOVED TO, where the person's avatar used to stand; a plain comment a speech bubble.
                // ⚠ THE ENTRY'S OWN STATE, not the item's: the endpoint is the tenant's state at the
                // time, and painting it with the todo's CURRENT state made every past step wear
                // today's colour. An endpoint with no colour or category gets the neutral chip;
                // nothing is ever derived from the word.
                const mark = isStateChange(comment)
                    ? stateDot(
                          {
                              status: comment.to!,
                              statusCategory: comment.toCategory,
                              statusColor: comment.toColor,
                              statusIcon: comment.toIcon,
                          },
                          "sm",
                      )
                    : el("span", { class: "pqm-comment-mark", "aria-hidden": "true" }, [
                          icon("chat", 16, "var(--color-ink-6)"),
                      ]);

                const entry: HTMLElement[] = [
                    el("div", { class: "pqm-comment-head" }, [
                        mark,
                        el("div", { class: "pqm-comment-who", text: comment.who }),
                        el("div", { class: "pqm-comment-when", text: formatActivityTime(comment.ts) }),
                    ]),
                ];
                if (comment.text) {
                    // ⚠ HTML, not text — the remark field is rich text and the server has already
                    // scrubbed it (pqfMobileRemarkHtml). `richTextHtml` scrubs it again on insert.
                    entry.push(richTextHtml(comment.text, "pqm-richtext pqm-comment-text"));
                }

                return el("div", { class: "pqm-comment" }, entry);
            }),
        ),
    ];
}

/**
 * The three facts that DESCRIBE the TODO — status, due date, priority — in one row, each shown ONCE
 * and changed where it is shown. They used to appear twice: in a line under the title, and again
 * in a section of their own with a "Change…" button.
 *
 * Who may change what is PQFORCE's answer, not the relation (`Todo.permissions`):
 * - the STATUS opens the status sheet when there is a move to make — `transitions` are already the
 *   moves PQFORCE lets this person make, so no further permission is involved;
 * - the DUE DATE and the PRIORITY need `update`.
 *
 * A control that cannot be used is plain text, so its look already says so. While the permissions
 * are not known yet (the TODO is being read whole), due date and priority stay plain.
 *
 * Both the due date and the priority are OPTIONAL on a real tenant: "No due date" and "No priority"
 * are sentences, never a dangling "Due" or a literal "null".
 */
/** A fact that is not set, as an icon alone — its words are its accessible name and its tooltip. */
function unsetMark(glyph: "calendarBlank" | "flag", label: string): HTMLElement {
    return el("span", { class: "pqm-detail-unset", role: "img", "aria-label": label, title: label }, [
        icon(glyph, 18, "currentColor"),
    ]);
}

function detailControls(todo: Todo, actions: TodoActions, now: number): HTMLElement[] {
    const mayUpdate = todo.permissions?.update === true && !todo.partial;
    const moves = todo.partial ? [] : movesOf(todo);

    const control = (label: string, content: Node[], onClick: (() => void) | null, extra = ""): HTMLElement => {
        if (!onClick) return el("span", { class: `pqm-control pqm-control-static ${extra}` }, content);
        const button = el("button", { class: `pqm-control ${extra}`, type: "button", "aria-label": label }, [
            ...content,
            el("span", { class: "pqm-control-chevron", "aria-hidden": "true" }, [chevronRight()]),
        ]);
        bind(button, "click", onClick);
        return button;
    };

    const status = control(
        t("mobileViewChangeStatus", "Change status"),
        [stateDot(todo, "lg", true), el("span", { class: "pqm-detail-status", text: todo.status })],
        moves.length > 0 ? actions.openStatusSheet : null,
        "pqm-control-status",
    );

    // The date alone, with its year — no "Due", no "Today": the row is about facts, not sentences.
    const due = dueFacts(todo.dueISO, now);
    const dueControl = control(
        todo.dueISO ? t("mobileViewChangeDueDate", "Change due date") : t("mobileViewSetDueDate", "Set a due date"),
        [
            // No date: an empty calendar, not the words "No due date" — the row has no room for them.
            todo.dueISO
                ? el("span", {
                      class: `pqm-detail-due${due.overdue ? " pqm-due-overdue" : ""}`,
                      text: dueDateText(todo.dueISO),
                  })
                : unsetMark("calendarBlank", t("mobileViewNoDueDate", "No due date")),
        ],
        mayUpdate
            ? () => {
                  // The picker mounts itself over the whole screen — see openDatePicker().
                  // `document.body` is the fallback for a detached render, which the tests do.
                  const host = dueControl.closest(".pqm-app") ?? document.body;
                  openDatePicker(host as HTMLElement, {
                      selected: todo.dueISO,
                      now,
                      onSave: (iso) => actions.setDueDate(iso),
                  });
              }
            : null,
    );

    // The same chip a list row carries, so a priority looks the same on both screens.
    const priority = control(
        t("mobileViewChangePriority", "Change priority"),
        [
            // No priority: an empty flag, not the words "No priority".
            priorityChip(todo.priority) ?? unsetMark("flag", t("mobileViewNoPriority", "No priority")),
        ],
        mayUpdate ? actions.openPrioritySheet : null,
    );

    // One line: status, due date, priority.
    const out = [el("div", { class: "pqm-detail-controls" }, [status, dueControl, priority])];
    // No move to make: say who is holding it, in one line, rather than an unexplained plain status.
    if (!todo.partial && moves.length === 0) {
        out.push(el("div", { class: "pqm-hint pqm-detail-nomove", text: noTransitionNote(todo) }));
    }
    return out;
}

/**
 * The description, under a heading of its own, with a pencil to change it.
 *
 * The pencil is offered where PQFORCE lets this person update the TODO AND the app can edit this
 * description: one written with formatting (`descriptionEditable === false`) is shown, but can
 * only be changed in PQFORCE — a pencil there would open a sheet that says exactly that. An empty
 * description is a section only for somebody who may write one ("No description" and the pencil);
 * for anybody else there is nothing to show.
 */
function descriptionSection(todo: Todo, actions: TodoActions): HTMLElement | null {
    const editable =
        todo.permissions?.update === true && !todo.partial && (!todo.description || todo.descriptionEditable !== false);
    if (!todo.description && !editable) return null;

    const label = t("mobileSheetTodoDescription", "Description");
    const head: HTMLElement[] = [el("div", { class: "pqm-kicker", text: label })];
    if (editable) {
        const edit = el(
            "button",
            {
                class: "pqm-title-edit",
                type: "button",
                "aria-label": t("mobileViewEditDescription", "Edit description"),
            },
            [icon("pencil", 16)],
        );
        bind(edit, "click", actions.openDetailsSheet);
        head.push(edit);
    }

    return el("div", { class: "pqm-section pqm-detail-desc-section" }, [
        el("div", { class: "pqm-section-head" }, head),
        todo.description
            ? // ⚠ HTML, like a comment: the description field is rich text, and PQFORCE even marks its
              // own with a `<!-- pqf-html-v2 -->` sentinel. The server scrubbed it; this scrubs again.
              richTextHtml(todo.description, "pqm-richtext pqm-detail-desc")
            : el("div", {
                  class: "pqm-detail-desc pqm-detail-desc-none",
                  text: t("mobileViewNoDescription", "No description"),
              }),
    ]);
}

export function renderDetail(
    todo: Todo,
    state: MobileState,
    actions: TodoActions,
    now: number = Date.now(),
): HTMLElement[] {
    // The title, and beside it the way to change it and the description — PQFORCE's `update`.
    const title: HTMLElement[] = [el("div", { class: "pqm-h pqm-detail-title", text: todo.title })];
    if (todo.permissions?.update === true && !todo.partial) {
        const edit = el(
            "button",
            {
                class: "pqm-title-edit",
                type: "button",
                "aria-label": t("mobileViewEditTitle", "Edit title and description"),
            },
            [icon("pencil", 18)],
        );
        bind(edit, "click", actions.openDetailsSheet);
        title.push(edit);
    }

    // While the TODO is being read whole (GET todos/{id}) — always for a row from "All TODOs", which
    // carries no activity — the body stays EMPTY: no title and state from the list row that are about
    // to be replaced; the screen fills in once, when everything has arrived.
    const loading = todo.partial === true || state.todoLoading === todo.id;
    const body: HTMLElement[] = [];
    // Empty while loading: the busy chip over the screen says so (the shell draws it).
    if (!loading) {
        body.push(
            el("div", { class: "pqm-section" }, [
                el("div", { class: "pqm-title-row" }, title),
                ...detailControls(todo, actions, now),
            ]),
        );
        // The description first, right under the controls — what the TODO is about — then its links.
        const description = descriptionSection(todo, actions);
        if (description) body.push(description);
        body.push(factsTable(todo, state, actions));
        body.push(activitySection(todo, state, actions));
    }

    // At the very bottom, apart from everything else: deleting, where PQFORCE grants it.
    if (!loading && todo.permissions?.delete === true && !todo.partial) {
        const remove = el("button", { class: "btn btn-secondary btn-block pqm-detail-delete", type: "button" }, [
            icon("trash", 18),
            el("span", { text: t("mobileViewDeleteTodo", "Delete TODO") }),
        ]);
        bind(remove, "click", actions.openDeleteSheet);
        body.push(remove);
    }

    // The header repeats the title in one line: the big one below scrolls away, and a long TODO
    // has to stay identifiable while its comments are being read.
    return [
        screenHeader(state, {
            title: todo.title,
            code: todo.code,
            onBack: actions.back,
            actions: [searchButton(state, actions)],
        }),
        el("div", { class: "pqm-scroll pqm-detail-body" }, body),
    ];
}

/** The detail of a TODO that is not here yet — a reload into a detail, before the lists arrive. */
export function renderDetailLoading(state: MobileState, actions: TodoActions): HTMLElement[] {
    return [
        screenHeader(state, { title: "", onBack: actions.back, actions: [searchButton(state, actions)] }),
        // Empty: the shell's busy chip says "Loading…".
        el("div", { class: "pqm-scroll pqm-detail-body" }, []),
    ];
}
